import { useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { AddressFields } from "../../components/AddressFields";
import { Field } from "../../components/form";
import { useI18n } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/messages";
import { api, ApiError, type NewsletterStatus, type Profile, type ProfileInput } from "../../lib/api";

const FIELDS: { key: keyof ProfileInput; label: MessageKey; autoComplete: string; type?: string }[] = [
  { key: "firstName", label: "profile.firstName", autoComplete: "given-name" },
  { key: "lastName", label: "profile.lastName", autoComplete: "family-name" },
  { key: "phone", label: "profile.phone", autoComplete: "tel", type: "tel" },
];

/** Mi cuenta › Mis datos: avatar, name, phone (WhatsApp), delivery address and email preferences. */
export function ProfileForm() {
  const { t } = useI18n();
  const auth = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [form, setForm] = useState<ProfileInput | null>(null);
  const [errors, setErrors] = useState<Record<string, MessageKey>>({});
  const [notice, setNotice] = useState<{ ok: boolean; text: MessageKey } | null>(null);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const [welcome, setWelcome] = useState<NewsletterStatus["coupon"]>(null);
  const loadWelcome = (signal?: AbortSignal) =>
    api
      .newsletter(signal)
      .then((n) => setWelcome(n.coupon))
      .catch(() => {});

  useEffect(() => {
    const ctrl = new AbortController();
    api
      .profile(ctrl.signal)
      .then((p) => {
        setProfile(p);
        const { name: _n, email: _e, avatarUrl: _a, ...input } = p;
        setForm(input);
      })
      .catch(() => {});
    void loadWelcome(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  if (!form || !profile) return null;
  const set = (key: keyof ProfileInput) => (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      const p = await api.saveProfile(form);
      setProfile(p);
      setErrors({});
      setNotice({ ok: true, text: "profile.saved" });
      void loadWelcome();
      await auth.refresh();
    } catch (err) {
      const fields = err instanceof ApiError ? ((err.details as { fields?: string[] } | undefined)?.fields ?? []) : [];
      setErrors(Object.fromEntries(fields.filter((f) => f === "firstName" || f === "phone").map((f) => [f, `profile.errors.${f}` as MessageKey])));
      setNotice({ ok: false, text: "profile.error" });
    } finally {
      setBusy(false);
    }
  };

  const avatar = async (blob: Blob | null) => {
    setNotice(null);
    try {
      const r = blob ? await api.uploadAvatar(blob) : await api.removeAvatar();
      setProfile({ ...profile, avatarUrl: r.avatarUrl });
      await auth.refresh();
    } catch {
      setNotice({ ok: false, text: "profile.avatarError" });
    }
  };

  return (
    <section className="profile" aria-labelledby="profile-title">
      <h2 id="profile-title" className="section-title">
        {t("profile.title")}
      </h2>
      <div className="profile-avatar">
        {profile.avatarUrl ? (
          <img src={profile.avatarUrl} alt={t("profile.avatar")} width={88} height={88} referrerPolicy="no-referrer" />
        ) : (
          <span className="avatar-placeholder" aria-hidden="true">
            {(form.firstName || profile.email).slice(0, 1).toUpperCase()}
          </span>
        )}
        <div>
          <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => e.target.files?.[0] && avatar(e.target.files[0])} />
          <button type="button" className="btn btn-outline btn-sm" onClick={() => file.current?.click()}>
            {t("profile.avatarChange")}
          </button>{" "}
          {profile.avatarUrl && (
            <button type="button" className="btn btn-link btn-sm" onClick={() => avatar(null)}>
              {t("profile.avatarRemove")}
            </button>
          )}
        </div>
      </div>
      <form onSubmit={save} noValidate className="profile-form">
        <div className="profile-grid">
          {FIELDS.map((f) => (
            <Field key={f.key} id={`pf-${f.key}`} label={t(f.label)} type={f.type ?? "text"} autoComplete={f.autoComplete} value={String(form[f.key])} onChange={set(f.key)} error={errors[f.key] ? t(errors[f.key]) : null} />
          ))}
        </div>
        <fieldset>
          <legend>{t("profile.address")}</legend>
          <AddressFields idPrefix="pf" value={form} onChange={(patch) => setForm({ ...form, ...patch })} show="all" />
        </fieldset>
        <fieldset>
          <legend>{t("profile.emails")}</legend>
          <label className="check">
            <input type="checkbox" checked={form.marketingOptIn} onChange={(e) => setForm({ ...form, marketingOptIn: e.target.checked })} /> {t("profile.marketing")}
          </label>
          <p className="muted small">{t("profile.marketingHint")}</p>
          {welcome && (
            <p className="nl-account">
              {welcome.used ? t("nl.accountUsed", { code: welcome.code }) : t("nl.accountCoupon", { code: welcome.code, pct: welcome.pct, date: welcome.endsAt ? welcome.endsAt.split("-").reverse().join("/") : "—" })}
              {!welcome.used && welcome.excluded.length > 0 && ` ${t("nl.excluded", { list: welcome.excluded.join(", ") })}`}
            </p>
          )}
        </fieldset>
        {notice && (
          <p role="status" className={notice.ok ? "notice-ok" : "field-error"}>
            {t(notice.text)}
          </p>
        )}
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {t("profile.save")}
        </button>
      </form>
    </section>
  );
}
